import React from 'react'

interface GaugeChartProps {
  value: number
  maxValue?: number
  mainText: string
  subText?: string
  size?: number
  primaryColor?: string
  gradientStartColor?: string
  gradientEndColor?: string
  backgroundStartColor?: string
  backgroundEndColor?: string
  gaugeId?: string
  ticksCount?: number
  shadowColor?: string
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  maxValue = 100,
  mainText,
  subText = '',
  size = 220,
  primaryColor = '#3699FF',
  gradientStartColor = '#3699FF',
  gradientEndColor = '#5ccdff',
  backgroundStartColor = '#e0e6ff',
  backgroundEndColor = '#f5f8ff',
  gaugeId = 'gaugeGradient',
  ticksCount = 10,
  shadowColor = 'rgba(54, 153, 255, 0.5)',
}) => {
  // Calculate percentage for the gauge
  const percentage = Math.min((value / maxValue) * 100, 100)

  // Calculate positions
  const center = size / 2
  const radius = (size / 2) * 0.86 // 95% of half size
  const outerCircleRadius = (size / 2) * 0.94 // 103% of half size
  const innerTickRadius = (size / 2) * 0.73 // 80% of half size
  const outerTickRadius = (size / 2) * 0.8 // 88% of half size

  // Calculate circumference and stroke-dasharray
  const circumference = 2 * Math.PI * radius
  const strokeDashArray = `${(percentage / 100) * circumference} ${circumference}`

  // Generate tick marks positions
  const generateTickMarks = () => {
    const ticks = []
    for (let i = 0; i < ticksCount; i++) {
      const angle = -90 + i * (180 / (ticksCount - 1))
      const x1 = center + innerTickRadius * Math.cos((angle * Math.PI) / 180)
      const y1 = center + innerTickRadius * Math.sin((angle * Math.PI) / 180)
      const x2 = center + outerTickRadius * Math.cos((angle * Math.PI) / 180)
      const y2 = center + outerTickRadius * Math.sin((angle * Math.PI) / 180)

      ticks.push({x1, y1, x2, y2})
    }
    return ticks
  }

  const ticks = generateTickMarks()

  // Calculate unique gradient IDs to avoid conflicts when multiple charts are rendered
  const backgroundGradientId = `${gaugeId}Background`
  const foregroundGradientId = `${gaugeId}Foreground`

  return (
    <div className={`gauge-chart position-relative`} style={{width: size, height: size}}>
      <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
        <div className='d-flex flex-column align-items-center'>
          <div className='fs-1 fw-boldest text-primary mb-1' style={{color: primaryColor}}>
            {value.toFixed(1)}%
          </div>
          <div className='fs-6 text-gray-600'>{subText}</div>
        </div>
      </div>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Outer decorative circle */}
        <circle
          cx={center}
          cy={center}
          r={outerCircleRadius}
          fill='none'
          stroke='#f1f1f4'
          strokeWidth='1'
          strokeDasharray='4 4'
        />

        {/* Background gradient for the gauge */}
        <defs>
          <linearGradient id={backgroundGradientId} x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor={backgroundStartColor} />
            <stop offset='100%' stopColor={backgroundEndColor} />
          </linearGradient>
        </defs>

        {/* Background circle with gradient */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill='none'
          stroke={`url(#${backgroundGradientId})`}
          strokeWidth='12'
        />

        {/* Progress circle with gradient animation */}
        <defs>
          <linearGradient id={foregroundGradientId} x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor={gradientStartColor} />
            <stop offset='100%' stopColor={gradientEndColor} />
          </linearGradient>
        </defs>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill='none'
          stroke={`url(#${foregroundGradientId})`}
          strokeWidth='12'
          strokeDasharray={strokeDashArray}
          strokeLinecap='round'
          transform={`rotate(-90 ${center} ${center})`}
          style={{
            transition: 'stroke-dasharray 1.5s ease-in-out',
            filter: `drop-shadow(0px 0px 6px ${shadowColor})`,
          }}
        />

        {/* Add tick marks around the gauge */}
        {ticks.map((tick, i) => (
          <line
            key={i}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke='#CED4DA'
            strokeWidth='2'
          />
        ))}
      </svg>
    </div>
  )
}

export default GaugeChart
